import React from 'react';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

interface WithServerDataGridProps {
  rows: any[];
  columns: GridColDef[];
  rowIdKey?: string;
  loading?: boolean;
  totalItems?: number;
  page: number;
  limit: number;
  onPageChange?: (page: number, limit: number) => void;
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  selectedRows?: string[];
  onRowSelectionChange?: (selected: string[]) => void;
  pageSizeOptions?: number[];
}

const withServerDataGrid = <P extends object>() => {
  const HOC: React.FC<P & WithServerDataGridProps> = ({
    rows,
    columns,
    rowIdKey = 'id',
    loading = false,
    totalItems,
    page,
    limit,
    onPageChange,
    onSortChange,
    selectedRows = [],
    pageSizeOptions = [5, 10, 20, 30],
    onRowSelectionChange,
  }) => {
    return (
      <div style={{ height: '75vh', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row[rowIdKey]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={loading}
          rowCount={totalItems}
          paginationMode="server"
          sortingMode="server"
          paginationModel={{
            page: page - 1,
            pageSize: limit,
          }}
          onPaginationModelChange={(model) => {
            onPageChange?.(model.page + 1, model.pageSize);
          }}
          onSortModelChange={(model: GridSortModel) => {
            if (onSortChange) {
              if (model.length > 0) {
                const { field, sort } = model[0];
                onSortChange(field, sort ?? 'asc');
              } else {
                onSortChange('name', 'asc'); // default sort fallback
              }
            }
          }}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(selection) => {
            onRowSelectionChange?.(selection as string[]);
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#ffffff', // bright white
              fontWeight: 'bold',
              color: '#000000', // pure black
              fontSize: '15px',
              borderBottom: '1px solid #e0e0e0',
              // fontFamily: 'Livic, sans-serif',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
              color: '#000000', // force black titles
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'visible',
              color: '#d0d0d0', // slightly darker for clearer lines
            },
            '& .MuiDataGrid-cell': {
              fontSize: '14px',
              color: '#333333',
              // fontFamily: 'Livic, sans-serif',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
              {
                outline: 'none', // removes blue focus ring
              },
          }}
          pageSizeOptions={pageSizeOptions}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'linear-progress',
            },
          }}
        />
      </div>
    );
  };

  return HOC;
};

export default withServerDataGrid;
